import { IconButton, Typography, Collapse, TextField, ListItem, Paper } from '@material-ui/core';
import List from '@material-ui/core/List';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ContentCopy from '@material-ui/icons/FileCopyOutlined';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { Box } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { _selectedTopic, _selectedTopicId, _topics, updateTopic } from '../../store/slices/topicSlice';
import { colors } from '../../theme/palette';
import { ViiisionSuccessSnackbar } from '../ViiisionSuccessSnackbar';
import { ViiisionTooltip } from '../ViiisionTooltip';
import { drawerCollapsedWidth } from './ViiisionDrawer';
import { downloadImage } from '../../util/downloadQrcode';
import PNGIcon from '../../theme/png.svg';
import SVGIcon from '../../theme/svg.svg';
import { FlatButton } from '../Buttons/FlatButton';
import {
	_redirects,
	_selectedRedirectId,
	_isCreating,
	createNewRedirect,
	updateRedirect,
	deleteRedirect,
} from '../../store/slices/redirectSlice';
import { _selectedOrganization } from '../../store/slices/organizationSlice';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		title: {
			backgroundColor: colors.grayLight20,
		},
		titleFont: {
			fontSize: 14,
			fontWeight: 'bold',
		},
		list: {
			paddingLeft: drawerCollapsedWidth,
			paddingTop: 0,
		},
		addRow: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			paddingLeft: drawerCollapsedWidth,
			paddingRight: 8,
			marginTop: 8,
			marginBottom: 8,
		},
		addHelp: {
			color: colors.neutralDark,
			fontSize: 16,
			whiteSpace: 'normal',
			width: '100%',
			marginBottom: 8,
		},
		download: {
			'&:hover': {
				backgroundColor: colors.purpleAccent20,
				color: colors.purpleAccent,
			},
			'&:active': {
				color: colors.pureWhite,
				backgroundColor: colors.purpleAccent,
			},
		},
		copy: {
			'&:hover': {
				backgroundColor: colors.tealAccent20,
				color: colors.tealAccent,
			},
			'&:active': {
				color: colors.pureWhite,
				backgroundColor: colors.tealAccent,
			},
		},
		actions: {
			display: 'flex',
		},
		qr: {
			width: '100%',
			padding: 15,
		},
		grow: {
			flexGrow: 1,
		},
		inlineInput: {
			'& .MuiOutlinedInput-root': {
				padding: '2px 8px',
			},
			'& .MuiInputBase-input': {
				padding: 0,
				fontSize: 14,
				fontWeight: 600,
			},
			width: '60%',
		},
		detailsInner: {
			display: 'flex',
			flexDirection: 'column',
			gap: 8,
			width: '100%',
		},
		deleteRow: {
			display: 'flex',
			justifyContent: 'flex-end',
		},
		column: {
			display: 'flex',
			flexDirection: 'column',
			gap: 8,
			width: '100%',
		},
		collapseHeader: {
			display: 'flex',
			alignItems: 'center',
			width: '100%',
			gap: 8,
			padding: '8px 16px',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: colors.grayLight20,
			},
		},
		collapseContent: {
			padding: '0 16px 16px 16px',
		},
		collapseItem: {
			marginBottom: 8,
			border: `1px solid ${colors.grayLight20}`,
			borderRadius: 4,
		},
	})
);

export const QrMenu: React.FC = () => {
	const store = useStore();
	const topic = useAppSelector(_selectedTopic);
	const topicId = useAppSelector(_selectedTopicId);
	const topics = useAppSelector(_topics);
	const redirects = useAppSelector(_redirects);
	const isCreatingRedirect = useAppSelector(_isCreating);
	const createdRedirectId = useAppSelector(_selectedRedirectId);
	const dispatch = useAppDispatch();
	const updateTopicAction = bindActionCreators(updateTopic, dispatch);
	const createRedirectAction = bindActionCreators(createNewRedirect, dispatch);
	const updateRedirectAction = bindActionCreators(updateRedirect, dispatch);
	const deleteRedirectAction = bindActionCreators(deleteRedirect, dispatch);
	const [copySuccess, setCopySuccess] = useState(false);
	const [expanded, setExpanded] = useState<string>('main');
	const [addNewCode, setaddNewCode] = useState(false);
	const [newName, setNewName] = useState('');
	const [newDescription, setNewDescription] = useState('');
	const [editingNameId, setEditingNameId] = useState<string>(null);
	const [editingDescId, setEditingDescId] = useState<string>(null);
	const [editingNameValue, setEditingNameValue] = useState<string>('');
	const [editingDescValue, setEditingDescValue] = useState<string>('');
	const [imageReloadKey, setImageReloadKey] = useState<number>(Date.now());
	const pendingCreateRef = useRef<{ name: string; description: string }>(null);
	const clickTimeoutRef = useRef<NodeJS.Timeout>(null);
	const classes = useStyles();
	const selectedOrganization = useAppSelector(_selectedOrganization);
	const appSlug = selectedOrganization?.appSlug;
	const pageSlug = topic?.metadata?.pageSlug;

	const topicLink = useMemo(() => {
		if (appSlug && pageSlug) {
			return `${process.env.REACT_APP_LIIINGO_WEBAPP_URL}/${appSlug}/${pageSlug}`;
		}
		return `${process.env.REACT_APP_LIIINGO_WEBAPP_URL}/topic/${topic.id}`;
	}, [appSlug, pageSlug, topic?.id]);

	const extraQRCodes: { id: string; name?: string; description?: string }[] = useMemo(() => {
		const arr = topic?.metadata?.extraQRCodes || [];
		return Array.isArray(arr) ? arr : [];
	}, [topic]);

	const copy = () => {
		// Get the latest state from Redux store at copy time
		const state = store.getState();
		const currentTopicId = state.topic.selectedTopicId;
		const currentTopic = state.topic.topics[currentTopicId];
		const currentOrg = state.organization.organizations[state.organization.selectedOrgId];
		const { appSlug } = currentOrg || {};
		const { pageSlug } = currentTopic?.metadata || {};
		const url =
			appSlug && pageSlug
				? `${process.env.REACT_APP_LIIINGO_WEBAPP_URL}/${appSlug}/${pageSlug}`
				: `${process.env.REACT_APP_LIIINGO_WEBAPP_URL}/topic/${currentTopic.id}`;
		navigator.clipboard.writeText(url);
		setCopySuccess(true);
	};

	const handleAddClick = () => {
		setNewName('');
		setNewDescription('');
		setaddNewCode(true);
	};

	const handleSaveNew = () => {
		if (!newName?.trim()) return;
		pendingCreateRef.current = { name: newName.trim(), description: newDescription?.trim() || '' };
		createRedirectAction({
			name: newName.trim(),
			url: topicLink,
			description: newDescription?.trim() || '',
		});
		setaddNewCode(false);
	};

	useEffect(() => {
		// When redirect creation completes, attach it to this topic's metadata and expand it
		if (!isCreatingRedirect && pendingCreateRef.current && createdRedirectId && redirects[createdRedirectId]) {
			const redirect = redirects[createdRedirectId];
			if (redirect?.url === topicLink && redirect?.name === pendingCreateRef.current.name) {
				const clonedTopic = structuredClone(topics[topicId]);
				const current = Array.isArray(clonedTopic.metadata?.extraQRCodes)
					? [...clonedTopic.metadata.extraQRCodes]
					: [];
				current.push({
					id: createdRedirectId,
					name: pendingCreateRef.current.name,
					description: pendingCreateRef.current.description,
				});
				clonedTopic.metadata = { ...(clonedTopic.metadata || {}), extraQRCodes: current };
				updateTopicAction(clonedTopic);
				setExpanded(createdRedirectId);
				pendingCreateRef.current = null;
			}
		}
	}, [isCreatingRedirect, createdRedirectId, redirects, topicLink, topics, topicId, updateTopicAction]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (clickTimeoutRef.current) {
				clearTimeout(clickTimeoutRef.current);
			}
		};
	}, []);

	const handleDelete = (id: string) => {
		const clonedTopic = structuredClone(topics[topicId]);
		const current = (clonedTopic.metadata?.extraQRCodes || []).filter((e) => e.id !== id);
		clonedTopic.metadata = { ...(clonedTopic.metadata || {}), extraQRCodes: current };
		updateTopicAction(clonedTopic);
		deleteRedirectAction(id);
		setExpanded('main');
	};

	const handleNameBlur = (id: string, value: string) => {
		setEditingNameId(null);
		setEditingNameValue('');
		if (!value?.trim()) return;

		// Update the redirect object
		updateRedirectAction({ id, name: value.trim() });

		// Update the topic metadata to keep it in sync
		const clonedTopic = structuredClone(topics[topicId]);
		const current = [...(clonedTopic.metadata?.extraQRCodes || [])];
		const idx = current.findIndex((e) => e.id === id);
		if (idx >= 0) {
			current[idx] = { ...current[idx], name: value.trim() };
			clonedTopic.metadata = { ...(clonedTopic.metadata || {}), extraQRCodes: current };
			updateTopicAction(clonedTopic);
		}
	};

	const handleDescBlur = (id: string, value: string) => {
		setEditingDescId(null);
		setEditingDescValue('');
		const clonedTopic = structuredClone(topics[topicId]);
		const current = [...(clonedTopic.metadata?.extraQRCodes || [])];
		const idx = current.findIndex((e) => e.id === id);
		if (idx >= 0) {
			current[idx] = { ...current[idx], description: value };
			clonedTopic.metadata = { ...(clonedTopic.metadata || {}), extraQRCodes: current };
			updateTopicAction(clonedTopic);
		}
	};

	const handleCollapseClick = (id: string) => {
		// Clear any pending timeout
		if (clickTimeoutRef.current) {
			clearTimeout(clickTimeoutRef.current);
			clickTimeoutRef.current = null;
		}

		// Set a small delay to allow for double-click detection
		clickTimeoutRef.current = setTimeout(() => {
			setExpanded(expanded === id ? null : id);
		}, 200);
	};

	const handleImageError = () => {
		// Reload image when it fails to load
		setImageReloadKey(Date.now());
	};

	return (
		<>
			{/* Collapse List */}
			<List className={classes.list}>
				{/* Main QR item */}
				<Paper className={classes.collapseItem} elevation={1}>
					<Box className={classes.collapseHeader} onClick={() => handleCollapseClick('main')}>
						<Typography className={classes.titleFont}>QR Code</Typography>
						<Box className={classes.grow} />
						<Box className={classes.actions}>
							<ViiisionTooltip message="Download" placement="bottom">
								<>
									<IconButton
										disableRipple
										size="small"
										className={classes.download}
										onClick={(e) => {
											e.stopPropagation();
											downloadImage(topic.id, topic.name[0].name, 'png');
										}}
									>
										<img src={PNGIcon} alt="PNG download icon" width="24" height="24" />
									</IconButton>
									<IconButton
										disableRipple
										size="small"
										className={classes.download}
										onClick={(e) => {
											e.stopPropagation();
											downloadImage(topic.id, topic.name[0].name, 'svg');
										}}
									>
										<img src={SVGIcon} alt="SVG download icon" width="24" height="24" />
									</IconButton>
								</>
							</ViiisionTooltip>
							<ViiisionTooltip message="Copy Link" placement="bottom">
								<IconButton
									disableRipple
									size="small"
									className={classes.copy}
									onClick={(e) => {
										e.stopPropagation();
										copy();
									}}
								>
									<ContentCopy fontSize="small" />
								</IconButton>
							</ViiisionTooltip>
						</Box>
						<ArrowDropDownIcon
							style={{
								transform: expanded === 'main' ? 'rotate(180deg)' : 'rotate(0deg)',
								transition: 'transform 0.2s ease-in-out',
							}}
						/>
					</Box>
					<Collapse in={expanded === 'main'}>
						<Box className={classes.collapseContent}>
							<ViiisionTooltip
								placement="right"
								message="Scan this code with a mobile device to view the page you're currently designing."
							>
								<img
									src={`${process.env.REACT_APP_LIIINGO_URL}/exhibit/qr?id=${topic?.id}&_t=${imageReloadKey}`}
									alt="QR Code"
									className={classes.qr}
									key={`main-qr-${imageReloadKey}`}
									onError={handleImageError}
								/>
							</ViiisionTooltip>
						</Box>
					</Collapse>
				</Paper>

				{/* Extra QR items */}
				{extraQRCodes.map((item) => {
					const redirect = redirects[item.id];
					const nameValue = redirect?.name || 'New Code';
					const descValue = item.description || '';
					return (
						<Paper key={item.id} className={classes.collapseItem} elevation={1}>
							<Box className={classes.collapseHeader} onClick={() => handleCollapseClick(item.id)}>
								{editingNameId === item.id ? (
									<input
										type="text"
										title="Name"
										className={classes.inlineInput}
										value={editingNameValue}
										onChange={(e) => setEditingNameValue(e.target.value)}
										onBlur={(e) => handleNameBlur(item.id, e.target.value)}
										onClick={(e) => e.stopPropagation()}
										onDoubleClick={(e) => e.stopPropagation()}
										autoFocus
									/>
								) : (
									<Typography
										className={classes.titleFont}
										onDoubleClick={(e) => {
											e.stopPropagation();
											// Clear any pending collapse timeout
											if (clickTimeoutRef.current) {
												clearTimeout(clickTimeoutRef.current);
												clickTimeoutRef.current = null;
											}
											setEditingNameValue(nameValue);
											setEditingNameId(item.id);
										}}
									>
										{nameValue}
									</Typography>
								)}
								<Box className={classes.grow} />
								<Box className={classes.actions}>
									<ViiisionTooltip message="Download" placement="bottom">
										<>
											<IconButton
												disableRipple
												size="small"
												className={classes.download}
												onClick={(e) => {
													e.stopPropagation();
													downloadImage(
														topic.id + `&extraQrCodeName=${encodeURIComponent(nameValue)}`,
														`${topic.name[0].name}-${nameValue}`,
														'png'
													);
												}}
											>
												<img src={PNGIcon} alt="PNG download icon" width="24" height="24" />
											</IconButton>
											<IconButton
												disableRipple
												size="small"
												className={classes.download}
												onClick={(e) => {
													e.stopPropagation();
													downloadImage(
														topic.id + `&extraQrCodeName=${encodeURIComponent(nameValue)}`,
														topic.name[0].name,
														'svg'
													);
												}}
											>
												<img src={SVGIcon} alt="SVG download icon" width="24" height="24" />
											</IconButton>
										</>
									</ViiisionTooltip>
									<ViiisionTooltip message="Copy Link" placement="bottom">
										<IconButton
											disableRipple
											size="small"
											className={classes.copy}
											onClick={(e) => {
												e.stopPropagation();
												// Get the latest state from Redux store at copy time
												const state = store.getState();
												const currentTopicId = state.topic.selectedTopicId;
												const currentTopic = state.topic.topics[currentTopicId];
												const currentOrg =
													state.organization.organizations[state.organization.selectedOrgId];
												const { appSlug } = currentOrg || {};
												const { pageSlug } = currentTopic?.metadata || {};
												const baseUrl =
													appSlug && pageSlug
														? `${process.env.REACT_APP_LIIINGO_WEBAPP_URL}/${appSlug}/${pageSlug}`
														: `${process.env.REACT_APP_LIIINGO_WEBAPP_URL}/topic/${currentTopic.id}`;
												const customLink = `${baseUrl}?name=${encodeURIComponent(
													nameValue.replace(/ /g, '-')
												)}`;
												navigator.clipboard.writeText(customLink);
												setCopySuccess(true);
											}}
										>
											<ContentCopy fontSize="small" />
										</IconButton>
									</ViiisionTooltip>
								</Box>
								<ArrowDropDownIcon
									style={{
										transform: expanded === item.id ? 'rotate(180deg)' : 'rotate(0deg)',
										transition: 'transform 0.2s ease-in-out',
									}}
								/>
							</Box>
							<Collapse in={expanded === item.id}>
								<Box className={classes.collapseContent}>
									<Box className={classes.detailsInner}>
										{editingDescId === item.id ? (
											<textarea
												className={classes.inlineInput}
												style={{ width: '100%', resize: 'none' }}
												rows={3}
												title="Description"
												placeholder="Description"
												value={editingDescValue}
												onChange={(e) => setEditingDescValue(e.target.value)}
												onBlur={(e) => handleDescBlur(item.id, e.target.value)}
												onClick={(e) => e.stopPropagation()}
												onDoubleClick={(e) => e.stopPropagation()}
												autoFocus
											/>
										) : (
											<Typography
												className={classes.addHelp}
												onDoubleClick={(e) => {
													e.stopPropagation();
													// Clear any pending collapse timeout
													if (clickTimeoutRef.current) {
														clearTimeout(clickTimeoutRef.current);
														clickTimeoutRef.current = null;
													}
													setEditingDescValue(descValue);
													setEditingDescId(item.id);
												}}
											>
												{descValue}
											</Typography>
										)}
										<img
											src={`${process.env.REACT_APP_LIIINGO_URL}/exhibit/qr?id=${
												topic?.id
											}&extraQrCodeName=${encodeURIComponent(nameValue)}&_t=${imageReloadKey}`}
											alt={nameValue}
											className={classes.qr}
											key={`extra-qr-${item.id}-${imageReloadKey}`}
											onError={handleImageError}
										/>
										<Box className={classes.deleteRow}>
											<FlatButton color="secondary" onClick={() => handleDelete(item.id)}>
												Delete
											</FlatButton>
										</Box>
									</Box>
								</Box>
							</Collapse>
						</Paper>
					);
				})}
				<ListItem>
					{addNewCode ? (
						<>
							<Box className={classes.column}>
								<Typography variant="h6">Name</Typography>
								<TextField
									fullWidth
									size="small"
									placeholder="flyer-1"
									variant="outlined"
									value={newName}
									onChange={(e) => setNewName(e.target.value)}
									style={{ marginBottom: 16 }}
								/>
								<Typography variant="h6">Description</Typography>
								<TextField
									fullWidth
									placeholder="How you're using this code"
									variant="outlined"
									multiline
									rows={3}
									value={newDescription}
									onChange={(e) => setNewDescription(e.target.value)}
								/>
								<FlatButton variant="contained" color="primary" onClick={handleSaveNew}>
									Save
								</FlatButton>
								<FlatButton variant="contained" onClick={() => setaddNewCode(false)}>
									Cancel
								</FlatButton>
							</Box>
						</>
					) : (
						<Box className={classes.column}>
							{extraQRCodes.length === 0 && (
								<Typography className={classes.addHelp}>
									Create additional codes to this page when you have multiple entry points.
								</Typography>
							)}

							<FlatButton size="small" variant="contained" color="primary" onClick={handleAddClick}>
								Add Code
							</FlatButton>
						</Box>
					)}
				</ListItem>
			</List>

			<ViiisionSuccessSnackbar
				open={copySuccess}
				text="Link copied successfully"
				onClose={() => setCopySuccess(false)}
			/>
		</>
	);
};
